/**
 * Day 3, confirmation: the Owner's decision reason, manual candidate entry,
 * replacing a confirmed competitor, and freezing exactly three for Day 4.
 */

import type { ReactNode } from 'react';

import {
  addAnalysisCompetitorManually,
  freezeAnalysisCompetitorSet,
  newIdempotencyKey,
  replaceAnalysisCompetitor,
  type AnalysisDay3State,
  type AnalysisRunSummary,
} from '../../api.js';
import { Button, TextAreaField, TextField } from '../../ui.js';
import { card, selectStyle } from './run-page-styles.js';
import type { RunMutation } from './useAnalysisRunMutation.js';
import type { CompetitorForms } from './useCompetitorForms.js';

export function CompetitorConfirmationPanel({
  id,
  run,
  day3,
  busy,
  mutate,
  forms,
}: {
  readonly id: string;
  readonly run: AnalysisRunSummary;
  readonly day3: AnalysisDay3State;
  readonly busy: string | null;
  readonly mutate: RunMutation;
  readonly forms: CompetitorForms;
}): ReactNode {
  const {
    competitorReason,
    setCompetitorReason,
    manualName,
    setManualName,
    manualUrl,
    setManualUrl,
    manualOffer,
    setManualOffer,
    manualGeography,
    setManualGeography,
    manualDelivery,
    setManualDelivery,
    manualAudience,
    setManualAudience,
    manualNeed,
    setManualNeed,
    manualNote,
    setManualNote,
    replacePrior,
    setReplacePrior,
    replaceWith,
    setReplaceWith,
  } = forms;

  return (
    <>
      {run.currentStage === 'COMPETITOR_CONFIRMATION' &&
      run.executionState === 'AWAITING_REVIEW' ? (
        <>
          <div style={{ ...card, marginTop: 12 }}>
            <TextAreaField
              label="Owner decision reason"
              value={competitorReason}
              onChange={setCompetitorReason}
              rows={2}
              maxLength={4000}
            />
          </div>
          <form
            style={{ ...card, marginTop: 12 }}
            onSubmit={(event) => {
              event.preventDefault();
              void mutate('manual-competitor', async () => {
                await addAnalysisCompetitorManually(id, newIdempotencyKey(), {
                  name: manualName,
                  canonicalUrl: manualUrl,
                  ...(manualOffer ? { offer: manualOffer } : {}),
                  ...(manualGeography ? { geography: manualGeography } : {}),
                  ...(manualDelivery ? { deliveryMode: manualDelivery } : {}),
                  ...(manualAudience ? { audience: manualAudience } : {}),
                  ...(manualNeed ? { need: manualNeed } : {}),
                  ...(manualNote ? { evidenceNote: manualNote } : {}),
                  ...(competitorReason ? { reason: competitorReason } : {}),
                });
                setManualName('');
                setManualUrl('');
                setManualOffer('');
                setManualGeography('');
                setManualDelivery('');
                setManualAudience('');
                setManualNeed('');
                setManualNote('');
              });
            }}
          >
            <h3 style={{ font: 'var(--type-heading-sm)' }}>Add competitor manually</h3>
            <p style={{ color: 'var(--text-muted)' }}>
              Manual entry still creates identity, evidence, qualification and review
              history.
            </p>
            <div className="mj-form-two-column">
              <TextField
                label="Business name"
                value={manualName}
                onChange={setManualName}
              />
              <TextField
                label="Website / Instagram / TikTok URL"
                value={manualUrl}
                onChange={setManualUrl}
                type="url"
                dir="ltr"
              />
              <TextField
                label="Observable Offer"
                value={manualOffer}
                onChange={setManualOffer}
              />
              <TextField
                label="Geography"
                value={manualGeography}
                onChange={setManualGeography}
              />
              <TextField
                label="Delivery mode"
                value={manualDelivery}
                onChange={setManualDelivery}
              />
              <TextField
                label="Audience"
                value={manualAudience}
                onChange={setManualAudience}
              />
              <TextField
                label="Customer need / outcome"
                value={manualNeed}
                onChange={setManualNeed}
              />
              <TextField
                label="Public evidence note"
                value={manualNote}
                onChange={setManualNote}
              />
            </div>
            <Button
              type="submit"
              variant="outline"
              busy={busy === 'manual-competitor'}
              disabled={!manualName.trim() || !manualUrl.trim()}
            >
              Add and qualify
            </Button>
          </form>

          {day3.candidates.some((candidate) => candidate.state === 'CONFIRMED') ? (
            <div style={{ ...card, marginTop: 12 }}>
              <h3 style={{ font: 'var(--type-heading-sm)' }}>
                Replace a confirmed competitor
              </h3>
              <div className="mj-form-two-column">
                <label>
                  <span>Replace</span>
                  <select
                    value={replacePrior}
                    onChange={(event) => {
                      setReplacePrior(event.target.value);
                    }}
                    style={selectStyle}
                  >
                    <option value="">Choose confirmed competitor</option>
                    {day3.candidates
                      .filter((candidate) => candidate.state === 'CONFIRMED')
                      .map((candidate) => (
                        <option key={candidate.businessId} value={candidate.businessId}>
                          {candidate.displayName}
                        </option>
                      ))}
                  </select>
                </label>
                <label>
                  <span>With</span>
                  <select
                    value={replaceWith}
                    onChange={(event) => {
                      setReplaceWith(event.target.value);
                    }}
                    style={selectStyle}
                  >
                    <option value="">Choose candidate</option>
                    {day3.candidates
                      .filter(
                        (candidate) =>
                          candidate.state === 'CANDIDATE' &&
                          candidate.qualificationState !== 'REJECTED',
                      )
                      .map((candidate) => (
                        <option key={candidate.businessId} value={candidate.businessId}>
                          {candidate.displayName}
                        </option>
                      ))}
                  </select>
                </label>
              </div>
              <Button
                variant="outline"
                disabled={!replacePrior || !replaceWith || !competitorReason.trim()}
                busy={busy === 'replace-competitor'}
                onClick={() =>
                  void mutate('replace-competitor', () =>
                    replaceAnalysisCompetitor(id, newIdempotencyKey(), {
                      priorCompetitorId: replacePrior,
                      replacementCompetitorId: replaceWith,
                      reason: competitorReason,
                    }),
                  )
                }
              >
                Replace
              </Button>
            </div>
          ) : null}

          <div style={{ ...card, marginTop: 12 }}>
            <strong>
              {
                day3.candidates.filter((candidate) => candidate.state === 'CONFIRMED')
                  .length
              }{' '}
              / 3 confirmed
            </strong>
            <p style={{ color: 'var(--text-muted)' }}>
              Freeze only three genuinely relevant competitors. Weak candidates are
              never promoted merely to fill a slot.
            </p>
            <Button
              variant="primary"
              busy={busy === 'freeze-competitors'}
              disabled={
                day3.candidates.filter((candidate) => candidate.state === 'CONFIRMED')
                  .length !== 3
              }
              onClick={() =>
                void mutate('freeze-competitors', () =>
                  freezeAnalysisCompetitorSet(id, newIdempotencyKey()),
                )
              }
            >
              Freeze confirmed set for Day 4
            </Button>
          </div>
        </>
      ) : null}
    </>
  );
}
